import React, { memo, useCallback, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { Icons, Modal, PageTitle } from '@components/common';

import { useMutateDashboardAssetStatusesExport } from '@hooks/dashboardPO/useMutateDashboardAssetStatusesExport';
import {
	useMutateDashboardAssetSummaryPerOrganizationsExport,
} from '@hooks/dashboardPO/useMutateDashboardAssetSummaryPerOrganizationsExport';
import {
	useMutateDashboardAssetTypesSummaryExport,
} from '@hooks/dashboardPO/useMutateDashboardAssetTypesSummaryExport';
import useQueryDashboardAssetsSummary from '@hooks/dashboardPO/useQueryDashboardAssetsSummary';
import useQueryDashboardAssetTypesSummary from '@hooks/dashboardPO/useQueryDashboardAssetTypesSummary';
import useQueryDashboardRentGeneralSummary from '@hooks/dashboardPO/useQueryDashboardRentGeneralSummary';
import useQueryDashboardRequestStatusesSummary from '@hooks/dashboardPO/useQueryDashboardRequestStatusesSummary';

import { dashboardPOAssetTypesTable, dashboardPOStatusesTable } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setPage,
	setPageSize,
	setSort as setAssetsSort,
} from '@slices/dashboardPOAssetTypesTable';
import { setSort as setStatusesSort } from '@slices/dashboardPOStatusesTable';

import { formatDate } from '@utils/date';

import { ISortBy, ITablePagination } from '@interfaces/components/table';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';

import { EAdminPanelPageTitles } from '@enums/common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import colors from '@constants/colors';

import DashboardPOAssetTypesTable from './DashboardPOAssetTypesTable';
import DashboardPOPerOrganizationTable from './DashboardPOPerOrganizationTable';
import DashboardPORequestsTable from './DashboardPORequestsTable';
import DashboardPOStatusesTable from './DashboardPOStatusesTable';
import DashboardStatisticSummary from './DashboardStatisticSummary';
import { getRangeTimestamp, getRequestsListData, getSummaryListData } from './utils';

import styles from './DashboardPO.module.scss';

const MODAL_WIDTH = '760px';
const MODAL_HEIGHT = '720px';

const DashboardPO = () => {
	const [isPerOrganizationTable, setIsPerOrganizationTable] = useState(false);

	const {
		fromTimestamp,
		currentTimestamp,
	} = getRangeTimestamp();

	const dispatch = useAppDispatch();

	const handleStatusesSort = useCallback((sortBy: ISortBy[]) => {
		dispatch(
			setStatusesSort({
				sortDirection: !!sortBy[0]?.desc,
				sortFieldName: sortBy[0]?.id,
			}),
		);
	}, [dispatch]);

	const handleAssetsSort = useCallback((sortBy: ISortBy[]) => {
		dispatch(
			setAssetsSort({
				sortDirection: !!sortBy[0]?.desc,
				sortFieldName: sortBy[0]?.id,
			}),
		);
	}, [dispatch]);

	const pageHandler = (page: number) => dispatch(setPage({ page }));
	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const {
		sortDirection: statusSortDirection,
		sortFieldName: statusSortFieldName,
	} = useAppSelector(dashboardPOStatusesTable);

	const {
		sortDirection: assetTypesSortDirection,
		sortFieldName: assetTypesSortFieldName,
		page,
		pageSize,
	} = useAppSelector(dashboardPOAssetTypesTable);

	const {
		dashboardRequestStatusesSummary,
		isLoadingDashboardRequestStatusesSummary,
	} = useQueryDashboardRequestStatusesSummary({
		sortDirection: statusSortDirection,
		sortFieldName: statusSortFieldName,
	});

	const {
		countOfRecords,
		dashboardDashboardAssetTypesSummary,
		isLoadingDashboardAssetTypesSummary,
	} = useQueryDashboardAssetTypesSummary({
		sortDirection: assetTypesSortDirection,
		sortFieldName: assetTypesSortFieldName,
		page,
		pageSize,
	});

	const {
		watch,
		setValue,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			from: fromTimestamp,
			to: currentTimestamp,
		},
	});

	const fromDate = watch('from');
	const toDate = watch('to');

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler: pageHandler,
		pageSizeHandler: pageSizeHandler,
	};

	const setFromDate = useCallback((date: number | null) => setValue('from', date), [setValue]);

	const setToDate = useCallback((date: number | null) => setValue('to', date), [setValue]);

	const fromText = useMemo(() => fromDate ? formatDate(fromDate) : '', [fromDate]);

	const toText = useMemo(() => toDate ? formatDate(toDate) : '', [toDate]);

	const { dashboardAssetsSummary, isLoadingDashboardAssetsSummary } = useQueryDashboardAssetsSummary();

	const { exportDashboardAssetSummaryPerOrganizations } = useMutateDashboardAssetSummaryPerOrganizationsExport();
	const { exportDashboardAssetTypesSummary } = useMutateDashboardAssetTypesSummaryExport();
	const { exportDashboardStatusesSummary } = useMutateDashboardAssetStatusesExport();

	const {
		dashboardRentGeneralSummary,
		isLoadingDashboardRentGeneralSummary,
	} = useQueryDashboardRentGeneralSummary({
		fromText,
		toText,
	});

	const allAssetsData = getSummaryListData(dashboardAssetsSummary);
	const requestsData = getRequestsListData(dashboardRentGeneralSummary);

	const closePerOrganizationTable = useCallback(() => setIsPerOrganizationTable(false), []);

	const openPerOrganizationTable = useCallback(() => setIsPerOrganizationTable(true), []);

	const handleExportDashboardPerOrganizations = useCallback(({ fileType }: IExportFileDTO) => {
		if (!fileType) return;

		exportDashboardAssetSummaryPerOrganizations({ fileType });
	}, [exportDashboardAssetSummaryPerOrganizations]);

	const handleExportAssetTypesSummary = useCallback(({ fileType }: IExportFileDTO) => {
		if (!fileType) return;

		exportDashboardAssetTypesSummary({ fileType });
	}, [exportDashboardAssetTypesSummary]);

	const handleExportStatusesSummary = useCallback(({ fileType }: IExportFileDTO) => {
		if (!fileType) return;

		exportDashboardStatusesSummary({ fileType });
	}, [exportDashboardStatusesSummary]);

	return (
		<div className={styles.assetsDashboardContainer}>
			<PageTitle
				icon={Icons.dashboardIcon({ color: colors.white })}
				title={EAdminPanelPageTitles.Dashboard}
			/>
			<div className={styles.detailsWrapper}>
				<DashboardStatisticSummary
					allAssetsData={allAssetsData}
					fromDate={fromDate}
					handleSummaryExport={handleExportDashboardPerOrganizations}
					isLoadingDashboardAssetsSummary={isLoadingDashboardAssetsSummary}
					isLoadingDashboardRentGeneralSummary={isLoadingDashboardRentGeneralSummary}
					openPerOrganizationTable={openPerOrganizationTable}
					requestsData={requestsData}
					setFromDate={setFromDate}
					setToDate={setToDate}
					statisticClassName={styles.statisticSummary}
					toDate={toDate}
				/>
				<div className={styles.details}>
					<DashboardPORequestsTable
						fromText={fromText}
						toText={toText}
					/>
				</div>
			</div>
			<div className={styles.assetsTableWrapper}>
				<DashboardPOStatusesTable
					dashboardRequestStatusesSummary={dashboardRequestStatusesSummary}
					handleExport={handleExportStatusesSummary}
					handleSort={handleStatusesSort}
					isLoadingDashboardRequestStatusesSummary={isLoadingDashboardRequestStatusesSummary}
					sortDirection={statusSortDirection}
					sortFieldName={statusSortFieldName}
				/>
				<DashboardPOAssetTypesTable
					dashboardDashboardAssetTypesSummary={dashboardDashboardAssetTypesSummary}
					handleExport={handleExportAssetTypesSummary}
					handleSort={handleAssetsSort}
					isLoadingDashboardAssetTypesSummary={isLoadingDashboardAssetTypesSummary}
					pagination={pagination}
					sortDirection={assetTypesSortDirection}
					sortFieldName={assetTypesSortFieldName}
				/>
			</div>
			<Modal
				height={MODAL_HEIGHT}
				isOpen={isPerOrganizationTable}
				isScrollableContent
				onClose={closePerOrganizationTable}
				title='List of Totals per Organisation'
				width={MODAL_WIDTH}
			>
				<DashboardPOPerOrganizationTable />
			</Modal>
		</div>
	);
};

export default memo(DashboardPO);
